import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import arresteeService from '../../../services/arresteeService';

const SearchPanel = ({ onSearch, loading }) => {
  const [searchForm, setSearchForm] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    facility: '',
    caseNumber: '',
    bookingNumber: '',
    fuzzy: true
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [diagnosticsResults, setDiagnosticsResults] = useState(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  const handleInputChange = (field, value) => {
    setSearchForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    // Filter out empty values
    const cleanSearchParams = Object.entries(searchForm)?.reduce((acc, [key, value]) => {
        // Check if value is a string before calling trim()
        if (typeof value === 'string' && value?.trim() !== '') {
          acc[key] = value?.trim();
        } else if (key === 'fuzzy') {
          acc[key] = value; // Keep boolean values
        }
        return acc;
      }, {});

    // Enable fuzzy search for Jose Jiminez and similar searches
    if (cleanSearchParams?.firstName?.toLowerCase()?.includes('jose') || 
        cleanSearchParams?.lastName?.toLowerCase()?.includes('jiminez')) {
      cleanSearchParams.fuzzy = true;
    }

    onSearch?.(cleanSearchParams);
  };

  const handleClear = () => {
    setSearchForm({
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      facility: '',
      caseNumber: '',
      bookingNumber: '',
      fuzzy: true
    });
    setDiagnosticsResults(null);
  };

  const handleDiagnostics = async () => {
    try {
      const result = await arresteeService?.diagnoseSearch?.(
        searchForm?.firstName || null,
        searchForm?.lastName || null
      );
      
      if (result?.success) {
        setDiagnosticsResults(result?.data || []);
        setShowDiagnostics(true);
      }
    } catch (error) {
      console.error('Diagnostics failed:', error);
    }
  };

  // Quick search buttons for common scenarios
  const handleQuickSearch = (searchType) => {
    let quickParams = {};
    
    switch (searchType) {
      case 'jose_jiminez':
        quickParams = {
          firstName: 'Jose',
          lastName: 'Jiminez',
          fuzzy: true
        };
        break;
      case 'john_smith':
        quickParams = {
          firstName: 'John',
          lastName: 'Smith',
          fuzzy: true
        };
        break;
      case 'maria_garcia':
        quickParams = {
          firstName: 'Maria',
          lastName: 'Garcia',
          fuzzy: true
        };
        break;
      default:
        return;
    }
    
    setSearchForm(prev => ({ ...prev, ...quickParams }));
    onSearch?.(quickParams);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground flex items-center space-x-2">
          <Icon name="Search" size={20} className="text-primary" />
          <span>Search Records</span>
        </h2>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-sm text-muted-foreground hover:text-foreground flex items-center space-x-1"
          >
            <Icon name={showAdvanced ? "ChevronUp" : "ChevronDown"} size={16} />
            <span>{showAdvanced ? 'Simple' : 'Advanced'}</span>
          </button>
          <button
            type="button"
            onClick={handleDiagnostics}
            className="text-sm text-muted-foreground hover:text-foreground flex items-center space-x-1"
            title="Run search diagnostics"
          >
            <Icon name="Activity" size={16} />
            <span>Diagnostics</span>
          </button>
        </div>
      </div>
      {/* Quick Search Buttons */}
      <div className="mb-4">
        <p className="text-sm text-muted-foreground mb-2">Quick Search:</p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleQuickSearch('jose_jiminez')}
            className="px-3 py-1 text-xs bg-primary/10 text-primary rounded-md hover:bg-primary/20 transition-colors"
          >
            Jose Jiminez
          </button>
          <button
            onClick={() => handleQuickSearch('john_smith')}
            className="px-3 py-1 text-xs bg-primary/10 text-primary rounded-md hover:bg-primary/20 transition-colors"
          >
            John Smith
          </button>
          <button
            onClick={() => handleQuickSearch('maria_garcia')}
            className="px-3 py-1 text-xs bg-primary/10 text-primary rounded-md hover:bg-primary/20 transition-colors"
          >
            Maria Garcia
          </button>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Basic Search Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              First Name
            </label>
            <Input
              type="text"
              value={searchForm?.firstName}
              onChange={(e) => handleInputChange('firstName', e?.target?.value)}
              placeholder="e.g., Jose, John"
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Last Name
            </label>
            <Input
              type="text"
              value={searchForm?.lastName}
              onChange={(e) => handleInputChange('lastName', e?.target?.value)}
              placeholder="e.g., Jiminez, Smith"
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Date of Birth
            </label>
            <Input
              type="date"
              value={searchForm?.dateOfBirth}
              onChange={(e) => handleInputChange('dateOfBirth', e?.target?.value)}
              className="w-full"
            />
          </div>
        </div>

        {/* Advanced Search Fields */}
        {showAdvanced && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 border-t border-border">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Facility
              </label>
              <Input
                type="text"
                value={searchForm?.facility}
                onChange={(e) => handleInputChange('facility', e?.target?.value)}
                placeholder="e.g., SAC_MAIN"
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Case Number
              </label>
              <Input
                type="text"
                value={searchForm?.caseNumber}
                onChange={(e) => handleInputChange('caseNumber', e?.target?.value)}
                placeholder="e.g., SC2025-002156"
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Booking Number
              </label>
              <Input
                type="text"
                value={searchForm?.bookingNumber}
                onChange={(e) => handleInputChange('bookingNumber', e?.target?.value)}
                placeholder="e.g., BK2025-789012"
                className="w-full"
              />
            </div>
          </div>
        )}

        {/* Fuzzy Search Option */}
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="fuzzy-search"
            checked={searchForm?.fuzzy}
            onChange={(e) => handleInputChange('fuzzy', e?.target?.checked)}
            className="rounded border-border text-primary focus:ring-primary focus:ring-offset-0"
          />
          <label htmlFor="fuzzy-search" className="text-sm text-foreground">
            Enable fuzzy matching (recommended for names like Jose Jiminez)
          </label>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-4">
          <Button
            type="submit"
            disabled={loading}
            className="flex items-center space-x-2"
          >
            {loading ? (
              <Icon name="Loader2" size={16} className="animate-spin" />
            ) : (
              <Icon name="Search" size={16} />
            )}
            <span>{loading ? 'Searching...' : 'Search'}</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleClear}
            disabled={loading}
            className="flex items-center space-x-2"
          >
            <Icon name="X" size={16} />
            <span>Clear</span>
          </Button>
        </div>
      </form>
      {/* Search Diagnostics Results */}
      {showDiagnostics && diagnosticsResults && (
        <div className="mt-6 p-4 bg-muted/30 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-foreground flex items-center space-x-2">
              <Icon name="Activity" size={16} className="text-accent" />
              <span>Search Diagnostics</span>
            </h3>
            <button
              onClick={() => setShowDiagnostics(false)}
              className="text-muted-foreground hover:text-foreground"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
          <div className="space-y-2">
            {diagnosticsResults?.map?.((result, index) => (
              <div key={index} className="flex justify-between text-sm">
                <span className="text-muted-foreground">{result?.diagnostic_type}:</span>
                <span className="text-foreground font-medium">
                  {result?.record_count} records
                  {result?.sample_data && result?.sample_data !== 'Total persons in database' && 
                   result?.sample_data !== 'Total active arrestee records' && (
                    <span className="ml-2 text-muted-foreground">({result?.sample_data})</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Search Tips */}
      <div className="mt-4 p-3 bg-accent/10 rounded-lg">
        <p className="text-sm text-muted-foreground">
          <Icon name="Info" size={14} className="inline mr-1" />
          <strong>Search Tips:</strong> Use fuzzy matching for better results with names. 
          Partial names work well (e.g., "Jose" will find "Jose Jiminez"). 
          Leave fields blank to search all records.
        </p>
      </div>
    </div>
  );
};

export default SearchPanel;